import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Megaphone, Send } from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import VendorService from '../../services/vendorService';
import SourcingRfpService from '../../services/sourcingRFPService';

const NOTIFICATION_METHODS = ['Email', 'SMS', 'Portal Notification'];

const buildLineItemsFromGeneratedContent = (generatedContent) => {
  if (!generatedContent || typeof generatedContent !== 'object') {
    return [];
  }

  const sections = ['scope', 'technical', 'eligibility', 'commercial'];
  const lines = [];

  sections.forEach((sectionKey) => {
    const section = generatedContent?.[sectionKey];
    const points = Array.isArray(section?.points) ? section.points : [];

    points.forEach((point) => {
      const text = String(point || '').trim();
      if (text) {
        lines.push({ description: text, requirement: section?.title || '' });
      }
    });
  });

  if (lines.length > 0) {
    return lines;
  }

  return [
    {
      description: 'AI-generated scope item',
      requirement: 'Review and refine before vendor response collection',
    },
  ];
};

const toDateTimeLocal = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const RfpPublishingSettings = ({ isNested = false, rfpId: propRfpId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: paramId } = useParams();
  const id = isNested ? propRfpId : paramId;
  const { success, error: showError, toasts, removeToast } = useToast();
  const isDraftPublishFlow = !id || id === 'new';
  const incomingPersistedRfpId =
    location.state?.persistedRfpId || location.state?.existingRfpId || null;

  const [loading, setLoading] = useState(false);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [rfp, setRfp] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [deadline, setDeadline] = useState('');
  const [notificationMethod, setNotificationMethod] = useState('Email');
  const [persistedRfpId, setPersistedRfpId] = useState(
    isDraftPublishFlow ? incomingPersistedRfpId : id,
  );
  const showErrorRef = useRef(showError);
  const createDraftInProgressRef = useRef(false);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const selectedCount = useMemo(
    () => selectedVendors.length,
    [selectedVendors],
  );

  const inputPayload = location.state?.inputPayload || {};
  const generatedContent = location.state?.generatedContent || null;
  const selectedTemplateName = location.state?.selectedTemplateName || '';
  const draftTimeline = location.state?.inputPayload?.timeline;

  const buildDraftPayload = useCallback(() => {
    const resolvedDeadline = deadline
      ? new Date(deadline).toISOString()
      : (() => {
          const d = new Date();
          d.setDate(d.getDate() + 14);
          return d.toISOString();
        })();

    return {
      title:
        String(inputPayload?.projectName || '').trim() ||
        String(selectedTemplateName || '').trim() ||
        'AI Generated RFP',
      scope:
        String(inputPayload?.scope || '').trim() ||
        String(generatedContent?.scope?.points?.join(' ') || '').trim() ||
        'Generated from AI flow',
      invitedVendors: selectedVendors,
      responseDeadline: resolvedDeadline,
      lineItems: buildLineItemsFromGeneratedContent(generatedContent),
      status: 'DRAFT',
    };
  }, [
    deadline,
    generatedContent,
    inputPayload?.projectName,
    inputPayload?.scope,
    selectedTemplateName,
    selectedVendors,
  ]);

  const ensureDraftPersisted = useCallback(async () => {
    if (persistedRfpId || createDraftInProgressRef.current) {
      return persistedRfpId;
    }

    createDraftInProgressRef.current = true;
    setIsCreatingDraft(true);
    try {
      const created = await SourcingRfpService.createRfp(buildDraftPayload());
      const createdRfpId = created?.data?._id;
      if (!createdRfpId) {
        throw new Error(
          'RFP draft was not created. Missing RFP id in response.',
        );
      }

      setPersistedRfpId(createdRfpId);
      setRfp(created?.data || null);

      const createdDeadline = created?.data?.responseDeadline;
      if (createdDeadline) {
        setDeadline(toDateTimeLocal(createdDeadline));
      }

      localStorage.setItem(
        'rfp_publish_draft_snapshot',
        JSON.stringify({
          savedAt: new Date().toISOString(),
          rfpId: createdRfpId,
          invitedVendors: selectedVendors,
          responseDeadline: deadline,
          notificationMethod,
          source: 'template-manual-generate',
          inputPayload: location.state?.inputPayload || {},
          templateVariableMap: location.state?.templateVariableMap || {},
        }),
      );

      return createdRfpId;
    } finally {
      createDraftInProgressRef.current = false;
      setIsCreatingDraft(false);
    }
  }, [
    buildDraftPayload,
    deadline,
    location.state,
    notificationMethod,
    persistedRfpId,
    selectedVendors,
  ]);

  useEffect(() => {
    if (!isDraftPublishFlow || persistedRfpId) return;

    ensureDraftPersisted().catch((err) => {
      console.error('[RfpPublishingSettings] draft create failed', {
        message: err?.message,
        status: err?.status,
        data: err?.data,
      });
      showErrorRef.current(
        err?.message || 'Failed to create AI RFP draft in DB',
      );
    });
  }, [isDraftPublishFlow, persistedRfpId, ensureDraftPersisted]);

  useEffect(() => {
    let cancelled = false;

    const normalizeVendorRows = (payload) => {
      const rawRows =
        payload?.data?.vendors || payload?.data || payload?.vendors || [];

      if (!Array.isArray(rawRows)) return [];

      const seenIds = new Set();
      return rawRows.filter((vendor) => {
        const vendorId = vendor?._id || vendor?.id;
        if (!vendorId || vendor?.isActive === false) return false;
        if (seenIds.has(vendorId)) return false;
        seenIds.add(vendorId);
        return true;
      });
    };

    const loadPublishVendors = async () => {
      try {
        const publishVendorsRes = await SourcingRfpService.getPublishVendors({
          limit: 500,
        });
        const rows = normalizeVendorRows(publishVendorsRes);
        if (rows.length > 0) return rows;
      } catch (publishErr) {
        console.warn(
          '[RfpPublishingSettings] publish vendor endpoint failed, falling back',
          {
            message: publishErr?.message,
            status: publishErr?.status,
            data: publishErr?.data,
          },
        );
      }

      const fallbackRes = await VendorService.getVendors({
        page: 1,
        limit: 500,
      });
      return normalizeVendorRows(fallbackRes);
    };

    const loadData = async () => {
      setLoading(true);
      try {
        const resolvedRfpId = isDraftPublishFlow ? persistedRfpId : id;
        const [vendorRes, rfpRes] = await Promise.all([
          loadPublishVendors(),
          resolvedRfpId
            ? SourcingRfpService.getRfpById(resolvedRfpId)
            : Promise.resolve({ data: null }),
        ]);

        if (cancelled) return;

        const vendorRows = Array.isArray(vendorRes) ? vendorRes : [];

        const rfpRow = rfpRes?.data || null;

        setVendors(vendorRows);
        setRfp(rfpRow);

        if (rfpRow) {
          const invited = Array.isArray(rfpRow.invitedVendors)
            ? rfpRow.invitedVendors.map((v) =>
                typeof v === 'string' ? v : v?._id || v?.id,
              )
            : [];
          setSelectedVendors(invited.filter(Boolean));
          setDeadline(toDateTimeLocal(rfpRow.responseDeadline));
        } else if (isDraftPublishFlow) {
          if (draftTimeline) {
            const tentative = new Date();
            tentative.setDate(tentative.getDate() + 14);
            setDeadline(toDateTimeLocal(tentative.toISOString()));
          }
        }
      } catch (err) {
        console.error('[RfpPublishingSettings] loadData failed', {
          message: err?.message,
          status: err?.status,
          data: err?.data,
          stack: err?.stack,
        });
        showErrorRef.current(
          err.message || 'Failed to load publishing settings data',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [id, persistedRfpId, isDraftPublishFlow, draftTimeline]);

  const toggleVendor = (vendorId) => {
    setSelectedVendors((prev) =>
      prev.includes(vendorId)
        ? prev.filter((idValue) => idValue !== vendorId)
        : [...prev, vendorId],
    );
  };

  const handlePublish = async () => {
    if (selectedVendors.length === 0) {
      showError('Select at least one vendor.');
      return;
    }

    if (!deadline) {
      showError('Deadline is required.');
      return;
    }

    setLoading(true);
    try {
      const targetRfpId =
        id || persistedRfpId || (await ensureDraftPersisted());
      if (!targetRfpId) {
        throw new Error('Unable to resolve target RFP for publish flow');
      }

      await SourcingRfpService.updateRfp(targetRfpId, {
        invitedVendors: selectedVendors,
        responseDeadline: new Date(deadline).toISOString(),
      });

      await SourcingRfpService.publishRfp(targetRfpId);

      success(
        `${isDraftPublishFlow ? 'RFP created and published' : 'RFP published'}. Vendor notifications queued via ${notificationMethod}.`,
      );
      navigate(`/sourcing/rfps/processing/${targetRfpId}`);
    } catch (err) {
      console.error('[RfpPublishingSettings] publish failed', {
        message: err?.message,
        status: err?.status,
        data: err?.data,
        stack: err?.stack,
      });
      showError(
        err.message ||
          'Failed to publish. Ensure RFP is in Draft stage and all required fields are valid.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!isNested && <PageBreadcrumb />}
      {!isNested && (
        <PageHeader
          title="RFP PUBLISHING"
          description="Configure vendor audience, deadline and notification method before publishing."
        />
      )}

      <div className={isNested ? "" : "page-container"}>
        <div
          className="card"
          style={{ border: '1px solid var(--border-color)', borderRadius: 0 }}
        >
          <div className="card-body" style={{ padding: '20px' }}>
            <div className="d-flex align-items-center gap-2 mb-3">
              <Megaphone size={16} />
              <h6 className="mb-0">Publishing Settings</h6>
              {isCreatingDraft && (
                <span
                  style={{ fontSize: '12px', color: 'var(--text-secondary)' }}
                >
                  Saving AI draft to DB...
                </span>
              )}
            </div>

            <div className="row g-3">
              <div className="col-lg-12">
                <label className="form-label">
                  Vendor list ({selectedCount} selected)
                </label>
                <div
                  style={{
                    border: '1px solid var(--border-color)',
                    padding: '10px',
                    maxHeight: '220px',
                    overflowY: 'auto',
                    borderRadius: '4px',
                  }}
                >
                  {vendors.length === 0 ? (
                    <span
                      style={{
                        fontSize: '13px',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      No vendors found.
                    </span>
                  ) : (
                    vendors.map((vendor) => {
                      const vendorId = vendor?._id || vendor?.id;
                      const checked = selectedVendors.includes(vendorId);
                      return (
                        <label
                          key={vendorId}
                          className="d-flex align-items-center gap-2 mb-2"
                          style={{ fontSize: '13px' }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleVendor(vendorId)}
                          />
                          <span>
                            {vendor?.name || vendor?.vendorName || 'Vendor'}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="col-lg-6">
                <label className="form-label">Deadline</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                />
              </div>

              <div className="col-lg-6">
                <label className="form-label">Notification method</label>
                <select
                  className="form-select"
                  value={notificationMethod}
                  onChange={(e) => setNotificationMethod(e.target.value)}
                >
                  {NOTIFICATION_METHODS.map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12">
                <div
                  style={{ fontSize: '13px', color: 'var(--text-secondary)' }}
                >
                  RFP: {rfp?.rfpNumber || id || 'Draft Template Flow'}{' '}
                  {rfp?.title ? `- ${rfp.title}` : ''}
                </div>
              </div>
              {/* Form Actions - Synced with Invoice Page Style */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  padding: '20px 24px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #e5e7eb',
                  margin: '0 -24px -24px -24px', // Offset card body padding
                  borderRadius: '0 0 8px 8px',
                }}
              >
                <button
                  type="button"
                  onClick={() => navigate('/sourcing/rfps')}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePublish}
                  disabled={loading || isCreatingDraft}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)',
                    color: 'white',
                    backgroundColor: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (loading || isCreatingDraft) ? 'not-allowed' : 'pointer',
                    opacity: (loading || isCreatingDraft) ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Send size={18} />
                  Publish
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default RfpPublishingSettings;
